import { noop } from 'lodash';
import { type ComponentType, memo, Suspense, useCallback } from 'react';

import type { Notification, NotificationTypes } from '@didaskill/domain/notifications/types';

import { NotificationListItemSkeleton } from './skeleton/NotificationListItemSkeleton';

export type NotificationListRendererProps<TNotification extends Notification = Notification> = {
  notification: TNotification;
  onMarkAsRead?: () => void;
  onMarkAsUnread?: () => void;
  onCloseNotifications?: () => void;
  onClick?: () => void;
};

export type NotificationListItemProps<T extends NotificationTypes> = {
  /** Generic notification. */
  notification: Notification;
  /** Mapping from notification.notificationType to a component rendering the notification. */
  rendererMapping: Record<T, ComponentType<NotificationListRendererProps>>;
  /** Callback when marking as read. */
  onMarkAsRead?: (notificationId: string) => void;
  /** Callback when marking as unread. */
  onMarkAsUnread?: (notificationId: string) => void;
  /** Closes the list of notifications. */
  onCloseNotifications?: () => void;
  /** Callback when the notification is clicked. */
  onClick?: () => void;
};

export const NotificationListItemBase = <T extends NotificationTypes>({
  notification,
  rendererMapping,
  onMarkAsRead = noop,
  onMarkAsUnread = noop,
  onCloseNotifications = noop,
  onClick = noop,
}: NotificationListItemProps<T>) => {
  const Component = rendererMapping[notification.notificationType];

  const onMarkAsReadProxy = useCallback(
    () => onMarkAsRead(notification.id),
    [onMarkAsRead, notification.id],
  );

  const onMarkAsUnreadProxy = useCallback(
    () => onMarkAsUnread(notification.id),
    [onMarkAsUnread, notification.id],
  );

  return Component ? (
    <Suspense fallback={<NotificationListItemSkeleton />}>
      <Component
        notification={notification}
        onMarkAsRead={onMarkAsReadProxy}
        onMarkAsUnread={onMarkAsUnreadProxy}
        onCloseNotifications={onCloseNotifications}
        onClick={onClick}
      />
    </Suspense>
  ) : null;
};

NotificationListItemBase.displayName = 'NotificationListItem';

export const NotificationListItem = memo(
  NotificationListItemBase,
) as typeof NotificationListItemBase;
